import { useQuery } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

export interface GroupInvoiceRow {
  qbo_invoice_id: string;
  doc_number: string | null;
  txn_date: string;
  due_date: string | null;
  total: number;
  balance: number;
  is_open: boolean;
  days_past_due: number;
  customer_id: string;
  store_name: string;
  store_code: string | null;
  is_master: boolean;
  /** A payment we made is pending/settling on this invoice — do not pay again. */
  payment_in_flight?: boolean;
  /** Every line is a tank/equipment rental (or it is an EQ-LSE- lease). */
  is_rental?: boolean;
  rental_kind?: 'equipment' | 'tank' | null;
  /** The OWNING store's autopay will charge this — never offer it for manual pay. */
  rental_autopay?: boolean;
  /** The store's own PO number, when the order behind this invoice had one. */
  po_number?: string | null;
}

export interface GroupInvoicesResponse {
  ok: true;
  is_group: boolean;
  as_of_date?: string;
  /** Paid invoices are listed from this date (open ones always are). */
  paid_since?: string;
  master?: { customer_id: string; name: string; group_brand: string | null };
  stores: Array<{
    customer_id: string;
    name: string;
    store_code: string | null;
    is_master: boolean;
  }>;
  totals?: {
    open_count: number;
    open_balance: number;
    past_due_count: number;
    past_due_balance: number;
  };
  invoices: GroupInvoiceRow[];
  paying_invoice_ids?: string[];
}

/**
 * Every store's invoices in one list — for the chain's accounting login.
 *
 * Returns `is_group: false` for everyone else (an ordinary customer, a store
 * manager, a chain login without the master-accounting designation), which is
 * how the Invoices page decides to render its normal single-account view.
 * That is a normal answer, not an error, so the query never retries into a
 * wall for the 99% of logins this doesn't apply to.
 */
export function useGroupInvoices() {
  return useQuery<GroupInvoicesResponse>({
    queryKey: ['group-invoices'],
    queryFn: () => adminFetch<GroupInvoicesResponse>('customer-group-invoices'),
    staleTime: 60 * 1000,
    retry: 1,
  });
}
